import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchWithCorpId, fetchWithCorpIdAndFormData, fetchBlobWithCorpId, asArray } from '../utils/api';
import { useAuth } from '../utils/auth';
import { Section, Field, FormLoading } from './ui/formkit';
import { useToast } from './ui/Toast';
import { useConfirm } from './ui/ConfirmDialog';
import { toDateInputValue } from '../utils/dateUtils';

// Shared with ExpenseTable's category column filter (issue #144).
export const EXPENSE_CATEGORIES = ['Travel', 'Meals', 'Lodging', 'Supplies', 'Mileage', 'Other'];

const DEFAULT_EXPENSE = {
  id: '', employeeId: '', expenseDate: '', category: 'Travel',
  amount: '', description: '', notes: '', status: 'Pending',
};

function ExpenseForm({ mode = 'create', expenseId, formRef, hideActions = false, onSuccess, onCancel }) {
  const { user, getUserRoles } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const empId = user?.empid;
  const isEmployee = getUserRoles().includes('employee') && !getUserRoles().some(r => ['admin', 'employer', 'super'].includes(r));
  const isEdit = mode === 'edit';

  const [formData, setFormData] = useState(() => ({ ...DEFAULT_EXPENSE }));
  const [employees, setEmployees] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [message, setMessage] = useState('');
  const [loadingRecord, setLoadingRecord] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  // Snapshot of the last saved state; drives the drawer's discard guard.
  // In edit mode it is set once the record loads.
  const initialSnapshotRef = useRef(
    isEdit ? null : JSON.stringify({ ...DEFAULT_EXPENSE })
  );

  // Staff pick who the expense belongs to; employees always file their own
  // (the backend forces this regardless of the payload).
  useEffect(() => {
    if (isEmployee) return;
    fetchWithCorpId('/employees?page=0&size=99999&sort=fullName')
      .then(data => setEmployees(asArray(data)))
      .catch(() => {});
  }, [isEmployee]);

  const loadReceipts = useCallback(() => {
    if (!expenseId) return;
    fetchWithCorpId(`/expenses/${expenseId}/receipts`)
      .then(data => setReceipts(asArray(data)))
      .catch(() => {});
  }, [expenseId]);

  // Load the expense + its receipts when editing.
  useEffect(() => {
    if (!isEdit || !expenseId) {
      setLoadingRecord(false);
      return;
    }
    let cancelled = false;
    setLoadingRecord(true);
    fetchWithCorpId(`/expenses/${expenseId}`)
      .then(data => {
        if (cancelled || !data) return;
        const normalized = {
          ...DEFAULT_EXPENSE,
          ...data,
          expenseDate: toDateInputValue(data.expenseDate),
        };
        setFormData(normalized);
        initialSnapshotRef.current = JSON.stringify(normalized);
      })
      .catch((error) => {
        if (!cancelled) setMessage('Could not load this expense: ' + error.message);
      })
      .finally(() => {
        if (!cancelled) setLoadingRecord(false);
      });
    loadReceipts();
    return () => { cancelled = true; };
  }, [isEdit, expenseId, loadReceipts]);

  const isDirty = initialSnapshotRef.current !== null
    && JSON.stringify(formData) !== initialSnapshotRef.current;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    setPendingFiles(Array.from(e.target.files || []));
  };

  const uploadFiles = async (id, files) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      await fetchWithCorpIdAndFormData(`/expenses/${id}/receipts`, fd, { method: 'POST' });
    }
  };

  const handleDownload = async (receiptId) => {
    try {
      const blob = await fetchBlobWithCorpId(`/expenses/${expenseId}/receipts/${receiptId}`);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    } catch {
      setMessage('Failed to open receipt.');
    }
  };

  const handleDeleteReceipt = async (receiptId) => {
    const ok = await confirm({
      title: 'Delete receipt',
      message: 'This removes the uploaded receipt file.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await fetchWithCorpId(`/expenses/${expenseId}/receipts/${receiptId}`, { method: 'DELETE' });
      loadReceipts();
    } catch {
      setMessage('Failed to delete receipt.');
    }
  };

  // Approved expenses are read-only for employees; the backend enforces this too.
  const locked = isEmployee && formData.status === 'Approved';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (locked || saving) return;
    setMessage('');
    setSaving(true);
    try {
      const url = isEdit && formData.id ? `/expenses/${formData.id}` : '/expenses';
      const method = isEdit ? 'PUT' : 'POST';
      const saved = await fetchWithCorpId(url, { method, body: JSON.stringify(formData) });
      const savedId = saved?.id || formData.id;
      if (pendingFiles.length > 0 && savedId) {
        await uploadFiles(savedId, pendingFiles);
      }
      initialSnapshotRef.current = JSON.stringify(formData);
      toast.success('Expense saved');
      onSuccess?.();
    } catch (error) {
      setMessage(error.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingRecord) {
    return <FormLoading label="Loading expense…" />;
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} data-dirty={isDirty ? 'true' : 'false'}>
      {message && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>{message}</div>
      )}
      {locked && (
        <div className="alert" style={{ marginBottom: '20px' }}>
          This expense is approved and can no longer be edited.
        </div>
      )}
      {formData.reviewNotes && (
        <div className="alert error" style={{ marginBottom: '20px' }}>
          Review note: {formData.reviewNotes}
        </div>
      )}
      <fieldset disabled={locked} style={{ border: 'none', margin: 0, padding: 0, minWidth: 0 }}>
      <Section title="Expense" description="What you spent and when.">
        {!isEmployee && (
          <Field label="Employee" span={2} required>
            <select name="employeeId" value={formData.employeeId || ''} onChange={handleChange} required>
              <option value="">Select employee</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.fullName}</option>)}
            </select>
          </Field>
        )}
        <Field label="Category" required>
          <select name="category" value={formData.category} onChange={handleChange} required>
            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Date" required>
          <input type="date" name="expenseDate" value={formData.expenseDate} onChange={handleChange} required />
        </Field>
        <Field label="Amount" required>
          <input type="number" step="0.01" min="0" name="amount" value={formData.amount} onChange={handleChange} required />
        </Field>
        <Field label="Status">
          <input type="text" name="status" value={formData.status || 'Pending'} readOnly
            style={{ background: 'var(--surface-soft)', cursor: 'not-allowed', color: 'var(--muted)' }} />
        </Field>
      </Section>

      <Section title="Details" description="Describe the expense." grid="1fr">
        <Field label="Description">
          <input type="text" name="description" value={formData.description} onChange={handleChange} />
        </Field>
        <Field label="Notes">
          <textarea name="notes" value={formData.notes} onChange={handleChange} rows={3} />
        </Field>
      </Section>

      <Section title="Receipts" description="Attach receipts (PNG, JPG, JPEG or PDF)." grid="1fr">
        <div style={{ gridColumn: 'span 1' }}>
          <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileSelect} />
          {pendingFiles.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--muted)' }}>
              {pendingFiles.length} file(s) ready to upload on save.
            </div>
          )}
          {isEdit && receipts.length > 0 && (
            <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0 }}>
              {receipts.map(r => (
                <li key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '6px 0', fontSize: '13px' }}>
                  <span className="table-link" onClick={() => handleDownload(r.id)}>{r.filename}</span>
                  <span style={{ color: 'var(--muted-soft)' }}>{r.contentType}</span>
                  <button type="button" className="btn-secondary" style={{ marginLeft: 'auto', padding: '2px 8px' }}
                    onClick={() => handleDeleteReceipt(r.id)}>Remove</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>
      </fieldset>

      {!hideActions && (
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {onCancel && <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>Cancel</button>}
        </div>
      )}
    </form>
  );
}

export default ExpenseForm;
